import { Scope, StackEntryFlags, ValueType } from '@emdzej/inpax-core';
import { Stack } from './stack.js';
/**
 * Execution context for VM - manages stack and variable scopes
 */
export class ExecutionContext {
    stack;
    globalVars;
    constPool;
    constructor(globalVars, constPool) {
        this.stack = new Stack();
        this.globalVars = globalVars;
        this.constPool = constPool;
    }
    // Live view to the stack's frame offset — used to be a cached field
    // but the stack is the source of truth now that FRAME and user CALL
    // update it independently.
    get frameOffset() {
        return this.stack.getFrameOffset();
    }
    // ============ Stack Helpers ============
    popString() {
        return String(this.stack.pop().value);
    }
    popInt() {
        return Math.floor(Number(this.stack.pop().value));
    }
    popReal() {
        return Number(this.stack.pop().value);
    }
    popBool() {
        return Boolean(this.stack.pop().value);
    }
    popRef() {
        return this.stack.pop();
    }
    pushString(value) {
        this.stack.push(Stack.createEntry(ValueType.String, value));
    }
    pushInt(value) {
        this.stack.push(Stack.createEntry(ValueType.Int, value));
    }
    pushReal(value) {
        this.stack.push(Stack.createEntry(ValueType.Real, value));
    }
    pushBool(value) {
        this.stack.push(Stack.createEntry(ValueType.Bool, value));
    }
    /**
     * Read the slot a ref points to. Handles the frame-pinned Local
     * case where `refInfo.index` is absolute, not frame-relative.
     * Other scopes go through `getVariable` as normal.
     */
    getByRef(refInfo) {
        if (refInfo.scope === Scope.Local) {
            return this.stack.get(refInfo.index);
        }
        return this.getVariable(refInfo.scope, refInfo.index);
    }
    /**
     * Write to the slot a ref points to. Handles the frame-pinned
     * Local case where `refInfo.index` is absolute, not frame-relative.
     * Other scopes go through `setVariable`.
     */
    setByRef(refInfo, value) {
        if (refInfo.scope === Scope.Local) {
            this.stack.set(refInfo.index, { ...value });
            return;
        }
        this.setVariable(refInfo.scope, refInfo.index, value);
    }
    /**
     * Set out parameter by reference. Thin wrapper around `setByRef`
     * with a clearer name at the call site.
     */
    setOutParam(ref, value) {
        if (!ref.refInfo)
            throw new Error('Expected reference for out parameter');
        this.setByRef(ref.refInfo, value);
    }
    // ============ Variable Access ============
    getVariable(scope, index) {
        switch (scope) {
            case Scope.Global:
                return this.globalVars[index];
            case Scope.Const:
                return this.constPool[index];
            case Scope.Local:
                return this.stack.get(this.frameOffset + index);
            case Scope.Screen:
            case Scope.Menu:
            case Scope.StateMachine:
                // UI handles are not stored variables — they encode (scope, index)
                // as a synthetic handle so PUSHREF/LOAD can push them onto the
                // stack and setscreen()/setmenu()/setstatemachine() can resolve
                // back to the IPO block by index. value mirrors the index so
                // popInt() also works for system functions that read the raw int.
                return {
                    type: ValueType.ULong,
                    flags: StackEntryFlags.ByValue,
                    value: index,
                    refInfo: { scope, index },
                };
            default:
                throw new Error(`Unsupported scope: 0x${scope.toString(16)}`);
        }
    }
    setVariable(scope, index, value) {
        switch (scope) {
            case Scope.Global:
                this.globalVars[index] = { ...value };
                break;
            case Scope.Local:
                this.stack.set(this.frameOffset + index, { ...value });
                break;
            case Scope.Const:
                throw new Error('Cannot assign to constant');
            case Scope.Screen:
            case Scope.Menu:
            case Scope.StateMachine:
                throw new Error(`Cannot assign to UI handle (scope 0x${scope.toString(16)})`);
            default:
                throw new Error(`Unsupported scope: 0x${scope.toString(16)}`);
        }
    }
    createRef(scope, index) {
        const entry = this.getVariable(scope, index);
        // Preserve the synthetic handle value for UI scopes so consumers that
        // read it as an int (popInt) get the index back; for storage scopes
        // the value comes from the actual variable cell and the ref is just
        // a write target, so value is null.
        const isUiHandle = scope === Scope.Screen || scope === Scope.Menu || scope === Scope.StateMachine;
        // Frame-pin `Scope.Local` refs at creation time — store the absolute
        // stack index. Callees writing back through this ref then bypass
        // frameOffset arithmetic in `setOutParam`. See `RefInfo` docstring
        // in `@emdzej/inpax-core` for the full reasoning.
        const refIndex = scope === Scope.Local ? this.frameOffset + index : index;
        return {
            type: entry.type,
            flags: StackEntryFlags.ByReference,
            value: isUiHandle ? entry.value : null,
            refInfo: { scope, index: refIndex },
        };
    }
    // ============ Frame Management ============
    pushFrame() {
        this.stack.pushFrame();
    }
    popFrame() {
        this.stack.popFrame();
    }
}
//# sourceMappingURL=execution-context.js.map